import { useEffect, useState } from "react";
import { AlertTriangle, Bell, BellRing, ExternalLink, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* Ana sayfa canlı sektör fiyatları: kaynak, birim, yayın/çekilme tarihi ve eski veri uyarısı
   her kalemde açıkça gösterilir. Fiyat üretilmez; kaynağı olmayan kalem "veri yok" görünür. */
export default function PriceCenter() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/public/prices").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/prices/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [user]);

  const tracked = (code) => alerts.find((a) => a.item_code === code);

  const toggleAlert = async (code) => {
    const existing = tracked(code);
    try {
      if (existing) {
        await api.delete(`/prices/alerts/${existing.id}`);
        setAlerts(alerts.filter((a) => a.id !== existing.id));
        toast.success("Fiyat takibi kapatıldı.");
      } else {
        const { data: created } = await api.post("/prices/alerts", { item_code: code, direction: "ANY" });
        setAlerts([...alerts, created]);
        toast.success("Fiyat değişince panel bildirimi ve e-posta alacaksınız.");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8" data-testid="price-center">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-wider font-semibold text-secondary">
            Sektör Fiyat Merkezi
          </p>
          <h2 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-primary sm:text-3xl">
            Canlı hayvan, karkas ve süt fiyatları
          </h2>
        </div>
        <span className="text-xs text-muted-foreground">
          Kaynaklar: {(data.sources || []).map((s) => s.short).join(" · ")}
        </span>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(data.items || []).map((item) => (
          <article key={item.item_code} data-testid={`price-card-${item.item_code}`}
            className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-bold text-primary">{item.item_label}</h3>
              {item.stale && (
                <span data-testid={`price-stale-${item.item_code}`}
                  className="flex items-center gap-1 rounded-full bg-accent/20 px-2.5 py-0.5 text-xs font-bold text-primary">
                  <AlertTriangle className="h-3.5 w-3.5" /> Eski veri
                </span>
              )}
            </div>
            {item.available ? (
              <>
                <p className="mt-3 text-2xl font-extrabold text-primary">
                  {new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(item.value)}
                  <span className="ml-1 text-sm font-semibold text-muted-foreground">{item.unit}</span>
                </p>
                <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <div>Kaynak: {item.source_name}</div>
                  <div>Yayın tarihi: {item.published_at ? item.published_at.slice(0, 10) : "kaynakta belirtilmemiş"}</div>
                  <div>Platforma alınma: {(item.fetched_at || "").slice(0, 10)}</div>
                  {item.manual && <div data-testid={`price-manual-${item.item_code}`}>Yönetici girişi ({item.manual_by})</div>}
                </dl>
                {item.source_url && (
                  <a href={item.source_url} target="_blank" rel="noreferrer"
                    data-testid={`price-source-${item.item_code}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary underline">
                    Kaynağa git <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {user && (
                  <button data-testid={`price-track-${item.item_code}`}
                    onClick={() => toggleAlert(item.item_code)}
                    className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                      tracked(item.item_code)
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-primary hover:bg-muted"
                    }`}>
                    {tracked(item.item_code)
                      ? <><BellRing className="h-3.5 w-3.5" /> Takiptesin — kapat</>
                      : <><Bell className="h-3.5 w-3.5" /> Fiyat değişince haber ver</>}
                  </button>
                )}
              </>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground" data-testid={`price-empty-${item.item_code}`}>
                {item.note}
              </p>
            )}
          </article>
        ))}
      </div>

      <p className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
        <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" /> {data.disclaimer}
      </p>
    </section>
  );
}
